import React from 'react';
import { useStyletron } from 'baseui';
import { Button, KIND as ButtonKind, SHAPE, SIZE } from 'baseui/button';
import { HeadingSmall } from 'baseui/typography';
import { Input } from 'baseui/input';
import { FormControl } from 'baseui/form-control';
import { ListItem } from 'baseui/list';
import { ChevronDown } from 'baseui/icon';
import { Tag } from 'baseui/tag';
import { Tabs, Tab } from 'baseui/tabs';
import { Textarea } from 'baseui/textarea';
import { Delete } from 'baseui/icon';
import { useNavigate, useParams } from 'react-router-dom';
import useStore from '../store';

function CustomSelect({ options, value, onChange, placeholder }) {
  const [css] = useStyletron();
  const [isOpen, setIsOpen] = React.useState(false);
  const selectedOption = options.find(opt => opt.id === value?.[0]?.id);

  return (
    <div className={css({
      position: 'relative',
      width: '100%'
    })}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px',
          border: '2px solid #F0F0F0',
          borderRadius: '4px',
          cursor: 'pointer',
          backgroundColor: '#fff',
          ':hover': {
            backgroundColor: '#F8F8F8'
          }
        })}
      >
        <div>
          {selectedOption ? (
            <div>
              <div>{selectedOption.label}</div>
              {selectedOption.sublabel && (
                <div className={css({
                  fontSize: '12px',
                  color: '#666'
                })}>
                  {selectedOption.sublabel}
                </div>
              )}
            </div>
          ) : (
            <div className={css({ color: '#999' })}>{placeholder}</div>
          )}
        </div>
        <ChevronDown size={24} />
      </div>
      {isOpen && (
        <div className={css({
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: '#fff',
          border: '2px solid #F0F0F0',
          borderRadius: '4px',
          marginTop: '4px',
          maxHeight: '300px',
          overflowY: 'auto',
          zIndex: 1000
        })}>
          {options.map(option => (
            <ListItem
              key={option.id}
              onClick={() => {
                onChange({ value: [option] });
                setIsOpen(false);
              }}
              overrides={{
                Root: {
                  style: {
                    cursor: 'pointer',
                    backgroundColor: option.id === selectedOption?.id ? '#F6F6F6' : '#fff',
                    ':hover': {
                      backgroundColor: '#F6F6F6'
                    }
                  }
                }
              }}
            >
              <div>
                <div>{option.label}</div>
                {option.sublabel && (
                  <div className={css({
                    fontSize: '12px',
                    color: '#666'
                  })}>
                    {option.sublabel}
                  </div>
                )}
              </div>
            </ListItem>
          ))}
        </div>
      )}
    </div>
  );
}

export function RequestEditor() {
  const [css] = useStyletron();
  const store = useStore();
  const navigate = useNavigate();
  const { requestId, tab = 'body' } = useParams();
  const requestFetchFailed = React.useRef(false);

  const [requestData, setRequestData] = React.useState({
    name: '',
    description: '',
    endpointId: '',
    queryParams: [],
    headers: [],
    body: {
      contentType: 'application/json',
      content: ''
    }
  });
  const [response, setResponse] = React.useState(null);

  const selectedEndpoint = store.endpoints.find(e => e.id === requestData.endpointId);

  // Load request data if editing
  React.useEffect(() => {
    if (requestId && !requestFetchFailed.current) {
      const request = store.requests.find(r => r.id === requestId);
      if (request) {
        setRequestData(request);
      } else if (store.requests.length > 0) {
        // If we have requests but can't find this one, it was probably deleted
        console.error('Request not found:', requestId);
        requestFetchFailed.current = true;
        navigate('/requests');
      }
    }
  }, [requestId, store.requests, navigate]);

  // Reset failed flag if store is reset or we switch to a new request
  React.useEffect(() => {
    requestFetchFailed.current = false;
  }, [requestId]);

  // Build full URL with query params
  const fullUrl = React.useMemo(() => {
    if (!selectedEndpoint) return '';
    const url = new URL(selectedEndpoint.url);
    requestData.queryParams.forEach(param => {
      if (param.name && param.value) {
        url.searchParams.set(param.name, param.value);
      }
    });
    return url.toString();
  }, [selectedEndpoint, requestData.queryParams]);

  const handleSaveRequest = async () => {
    try {
      if (!requestData.name || !requestData.endpointId) {
        alert('Please fill in all required fields');
        return;
      }

      if (requestId) {
        await store.updateRequest(requestId, requestData);
      } else {
        await store.createRequest(requestData);
      }
      navigate('/requests');
    } catch (error) {
      console.error('Failed to save request:', error);
      alert('Failed to save request. Please try again.');
    }
  };

  const handleExecuteRequest = async () => {
    try {
      setResponse(null);
      const result = await store.executeRequest(requestData);
      setResponse(result);
    } catch (error) {
      console.error('Failed to execute request:', error);
      setResponse({
        error: true,
        message: error.message || 'Failed to execute request'
      });
    }
  };

  const addParam = (type) => {
    const field = type === 'query' ? 'queryParams' : 'headers';
    setRequestData({
      ...requestData,
      [field]: [...requestData[field], { name: '', value: '' }]
    });
  };

  const updateParam = (type, index, field, value) => {
    const arrayField = type === 'query' ? 'queryParams' : 'headers';
    const newArray = [...requestData[arrayField]];
    newArray[index] = { ...newArray[index], [field]: value };
    setRequestData({ ...requestData, [arrayField]: newArray });
  };

  const removeParam = (type, index) => {
    const field = type === 'query' ? 'queryParams' : 'headers';
    setRequestData({
      ...requestData,
      [field]: requestData[field].filter((_, i) => i !== index)
    });
  };

  const handleTabChange = ({ activeKey }) => {
    navigate(`/requests/${requestId || 'new'}/${activeKey.toLowerCase()}`);
  };

  return (
    <div className={css({
      flex: 1,
      padding: '24px',
      overflowY: 'auto'
    })}>
      <div className={css({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      })}>
        <HeadingSmall margin="0">
          {requestId ? 'Edit Request' : 'New Request'}
        </HeadingSmall>
        <div className={css({ display: 'flex', gap: '8px' })}>
          <Button
            onClick={handleSaveRequest}
            kind={ButtonKind.secondary}
          >
            Save
          </Button>
          <Button
            onClick={handleExecuteRequest}
            disabled={!requestData.endpointId}
          >
            Send
          </Button>
        </div>
      </div>

      <FormControl label="Name *">
        <Input
          value={requestData.name}
          onChange={e => setRequestData({ ...requestData, name: e.target.value })}
          placeholder="My Request"
        />
      </FormControl>

      <FormControl label="Description">
        <Input
          value={requestData.description}
          onChange={e => setRequestData({ ...requestData, description: e.target.value })}
          placeholder="Optional description"
        />
      </FormControl>

      <FormControl label="Endpoint *">
        <CustomSelect
          options={store.endpoints.map(endpoint => ({
            label: endpoint.name,
            sublabel: `${endpoint.method} ${endpoint.path}`,
            id: endpoint.id
          }))}
          value={requestData.endpointId ? [{
            label: selectedEndpoint?.name,
            sublabel: `${selectedEndpoint?.method} ${selectedEndpoint?.path}`,
            id: requestData.endpointId
          }] : []}
          onChange={({ value }) => setRequestData({
            ...requestData,
            endpointId: value[0]?.id
          })}
          placeholder="Select endpoint"
        />
      </FormControl>

      {selectedEndpoint && (
        <div className={css({
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#F6F6F6',
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '14px',
          wordBreak: 'break-all'
        })}>
          <div className={css({
            color: '#666',
            marginBottom: '4px'
          })}>
            {selectedEndpoint.method} {fullUrl}
          </div>
        </div>
      )}

      <Tabs
        activeKey={tab}
        onChange={handleTabChange}
        overrides={{
          Root: {
            style: {
              marginTop: '24px'
            }
          }
        }}
      >
        <Tab title="Body" key="body">
          <div className={css({ padding: '16px 0' })}>
            <FormControl label="Content Type">
              <CustomSelect
                options={[
                  { label: 'application/json', id: 'application/json' },
                  { label: 'application/x-www-form-urlencoded', id: 'application/x-www-form-urlencoded' },
                  { label: 'text/plain', id: 'text/plain' }
                ]}
                value={[{
                  label: requestData.body.contentType,
                  id: requestData.body.contentType
                }]}
                onChange={({ value }) => setRequestData({
                  ...requestData,
                  body: {
                    ...requestData.body,
                    contentType: value[0]?.id
                  }
                })}
                placeholder="Select content type"
              />
            </FormControl>
            <FormControl label="Content">
              <Textarea
                value={requestData.body.content}
                onChange={e => setRequestData({
                  ...requestData,
                  body: {
                    ...requestData.body,
                    content: e.target.value
                  }
                })}
                placeholder={requestData.body.contentType === 'application/json' ? 
                  '{\n  "key": "value"\n}' : 'Request body'}
                overrides={{
                  Input: {
                    style: {
                      height: '300px',
                      fontFamily: 'monospace'
                    }
                  }
                }}
              />
            </FormControl>
          </div>
        </Tab>
        <Tab title="Headers" key="headers">
          <div className={css({ padding: '16px 0' })}>
            {requestData.headers.map((header, index) => (
              <div key={index} className={css({
                display: 'flex',
                gap: '8px',
                marginBottom: '8px'
              })}>
                <Input
                  value={header.name}
                  onChange={e => updateParam('header', index, 'name', e.target.value)}
                  placeholder="Header name"
                  size={SIZE.compact}
                />
                <Input
                  value={header.value}
                  onChange={e => updateParam('header', index, 'value', e.target.value)}
                  placeholder="Value"
                  size={SIZE.compact}
                />
                <Button
                  onClick={() => removeParam('header', index)}
                  kind={ButtonKind.tertiary}
                  shape={SHAPE.round}
                  size={SIZE.compact}
                >
                  <Delete size={16} />
                </Button>
              </div>
            ))}
            <Button
              onClick={() => addParam('header')}
              size={SIZE.compact}
              kind={ButtonKind.secondary}
            >
              Add Header
            </Button>
          </div>
        </Tab>
        <Tab title="Query Params" key="params">
          <div className={css({ padding: '16px 0' })}>
            {requestData.queryParams.map((param, index) => (
              <div key={index} className={css({
                display: 'flex',
                gap: '8px',
                marginBottom: '8px'
              })}>
                <Input
                  value={param.name}
                  onChange={e => updateParam('query', index, 'name', e.target.value)}
                  placeholder="Parameter name"
                  size={SIZE.compact}
                />
                <Input
                  value={param.value}
                  onChange={e => updateParam('query', index, 'value', e.target.value)}
                  placeholder="Value"
                  size={SIZE.compact}
                />
                <Button
                  onClick={() => removeParam('query', index)}
                  kind={ButtonKind.tertiary}
                  shape={SHAPE.round}
                  size={SIZE.compact}
                >
                  <Delete size={16} />
                </Button>
              </div>
            ))}
            <Button
              onClick={() => addParam('query')}
              size={SIZE.compact}
              kind={ButtonKind.secondary}
            >
              Add Parameter
            </Button>
          </div>
        </Tab>
      </Tabs>

      {response && (
        <div className={css({
          marginTop: '24px',
          padding: '16px',
          backgroundColor: response.error ? '#FFF3F3' : '#F6F6F6',
          borderRadius: '8px'
        })}>
          <HeadingSmall margin="0">Response</HeadingSmall>
          {response.loading ? (
            <div>Loading...</div>
          ) : response.error ? (
            <div className={css({ color: 'red' })}>{response.message}</div>
          ) : (
            <>
              <div className={css({
                display: 'flex',
                gap: '8px',
                marginTop: '8px',
                marginBottom: '16px'
              })}>
                <Tag
                  kind={response.status >= 400 ? 'negative' : 'positive'}
                  closeable={false}
                >
                  {response.status} {response.statusText}
                </Tag>
              </div>
              <Tabs>
                <Tab title="Body">
                  <pre className={css({
                    margin: '16px 0',
                    padding: '16px',
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    overflow: 'auto'
                  })}>
                    {response.body}
                  </pre>
                </Tab>
                <Tab title="Headers">
                  <div className={css({ padding: '16px 0' })}>
                    {Object.entries(response.headers).map(([key, value]) => (
                      <div key={key} className={css({
                        display: 'flex',
                        marginBottom: '8px'
                      })}>
                        <div className={css({
                          fontWeight: 500,
                          marginRight: '8px',
                          minWidth: '200px'
                        })}>
                          {key}:
                        </div>
                        <div>{value}</div>
                      </div>
                    ))}
                  </div>
                </Tab>
              </Tabs>
            </>
          )}
        </div>
      )}
    </div>
  );
}
